import Link from "next/link";

/** Eine Kachel der Reihe: Ziel, Symbol (Lucide, vom Aufrufer), Name und eine Zählzeile. */
export interface UnterortKachel {
  /** Stabile Kennung (Schlüssel, Test-Anker). */
  id: string;
  titel: string;
  href: string;
  /** Lucide-Symbol des Aufrufers (R-F), wird als dekorativ markiert. */
  icon: React.ReactNode;
  /** Zählzeile unter dem Namen, z. B. „4 Geräte · 3 Dosen“ (fertiger Satz, vom Aufrufer aus einer Quelle). */
  zeile: string;
}

/**
 * Unterort-Reihe (Musterkatalog M-186, Design-Guide `unterort-reihe.md`): die Unterorte eines Ortes als waagerechte, scrollbare Reihe schmaler
 * Kacheln mit Zählzeile, darüber die Überschrift und rechts ein Link „Alle n …“ zur vollständigen Liste. Der Baustein kennt keine Fachregeln und
 * prüft keine Rechte: WELCHE Kacheln es gibt, entscheidet der Aufrufer mit der Prüfung des Laders (r110); nicht lesbare Objekte fehlen still.
 * Keine Kacheln: der Aufrufer übergibt den Leersatz, die Reihe zeigt ihn statt der Kacheln.
 */
export function UnterortReihe({
  id,
  titel,
  alleText,
  alleHref,
  kacheln,
  leerText,
}: {
  id?: string;
  titel: string;
  /** Text des Links rechts oben („Alle 14 Räume“); fehlt er (oder `alleHref`), steht kein Link. */
  alleText?: string;
  alleHref?: string;
  kacheln: UnterortKachel[];
  leerText: string;
}) {
  const titelId = id ? `${id}-titel` : undefined;
  return (
    <>
      {/* Preline: feedback-and-states/feedback-and-states-sub-actions-engagement/feedback-and-states-cat-action-cards-and-shortcut-links/simple-icon-shortcut-card-grid-with-descriptions */}
      <section id={id} aria-labelledby={titelId}>
        <div className="mb-2 flex justify-between items-center gap-x-3">
          <h2 id={titelId} className="font-semibold text-foreground">
            {titel}
          </h2>
          {alleText && alleHref && (
            <Link href={alleHref} className="text-sm text-primary hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus">
              {alleText}
            </Link>
          )}
        </div>
        {kacheln.length === 0 ? (
          <p className="text-sm text-muted-foreground-1" role="status">
            {leerText}
          </p>
        ) : (
          <ul className="flex gap-3 overflow-x-auto pb-1.5">
            {kacheln.map((k) => (
              <li key={k.id} className="shrink-0">
                <Link
                  href={k.href}
                  className="block w-56 p-1 bg-layer rounded-xl shadow-xs hover:bg-muted-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus"
                >
                  <div className="p-3 flex items-start gap-x-2 rounded-lg">
                    <span className="shrink-0 mt-0.5 inline-flex size-4 text-foreground" aria-hidden="true">
                      {k.icon}
                    </span>
                    <div className="grow min-w-0">
                      <p className="truncate font-medium text-sm text-foreground">{k.titel}</p>
                      <p className="text-xs text-muted-foreground-1">{k.zeile}</p>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
